import { GameState } from '../StateManager';
import { Game } from '../Game';
import { COLORS } from '../config/settings';

export class MenuState implements GameState {
  private game: Game;
  private selectedOption: number = 0;
  private options: string[] = [];
  private showInstructions: boolean = false;
  private animTimer: number = 0;
  private logoScale: number = 1;
  private logoY: number = 0;
  private splashImage: HTMLImageElement | null = null;
  private imageLoaded: boolean = false;
  private backtraxLogo: HTMLImageElement | null = null;
  private backtraxLogoLoaded: boolean = false;

  constructor(game: Game) {
    this.game = game;
    
    // Load splash screen image
    this.splashImage = new Image();
    this.splashImage.src = 'assets/background/HANDS_ACROSS_Splash1.png';
    this.splashImage.onload = () => {
      this.imageLoaded = true;
    };
    
    // Load Backtrax logo
    this.backtraxLogo = new Image();
    this.backtraxLogo.src = 'assets/backtrax_logo.png';
    this.backtraxLogo.onload = () => {
      this.backtraxLogoLoaded = true;
    };
  }

  public enter(): void {
    console.log('Entered Menu State');
    this.selectedOption = 0;
    this.showInstructions = false;
    this.logoY = -100;
    
    // Show start button
    const startButton = document.getElementById('startButton');
    if (startButton) {
      startButton.classList.remove('hidden');
    }
    
    // Listen for start game event from button
    window.addEventListener('startGame', this.handleStartGame);
  }

  public exit(): void {
    console.log('Exited Menu State');
    window.removeEventListener('startGame', this.handleStartGame);
  }
  
  private handleStartGame = (): void => {
    this.game.getStateManager().changeState('play');
  };

  public update(deltaTime: number): void {
    this.animTimer += deltaTime;

    // Animate logo drop-in
    if (this.logoY < 80) {
      this.logoY += deltaTime * 150;
    }
    this.logoScale = 1 + Math.sin(this.animTimer * 2) * 0.05;

    const input = this.game.getInput();

    if (this.showInstructions) {
      if (input.enter || input.escape) {
        this.showInstructions = false;
      }
      return;
    }

    // Navigate menu
    if (input.up) {
      this.selectedOption = (this.selectedOption - 1 + this.options.length) % this.options.length;
      // Prevent key repeat
      setTimeout(() => {}, 200);
    }

    if (input.down) {
      this.selectedOption = (this.selectedOption + 1) % this.options.length;
      setTimeout(() => {}, 200);
    }

    if (input.enter || input.fire) {
      this.selectOption();
    }
  }

  private selectOption(): void {
    switch (this.selectedOption) {
      case 0: // Instructions
        this.showInstructions = true;
        break;
      case 1: // High Scores
        // TODO: Show high scores
        break;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    const renderer = this.game.getRenderer();
    
    // Always draw gradient background
    const gradient = ctx.createLinearGradient(0, 0, renderer.width, renderer.height);
    gradient.addColorStop(0, '#1a0a2e');
    gradient.addColorStop(0.3, '#16213e');
    gradient.addColorStop(0.6, '#0f3460');
    gradient.addColorStop(1, '#0a0a0f');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, renderer.width, renderer.height);
    
    // Draw transparent splash image on top if loaded
    if (this.imageLoaded && this.splashImage) {
      // Calculate scaling to fit nicely in the canvas
      const maxWidth = renderer.width * 0.6;
      const maxHeight = renderer.height * 0.5;
      const imgAspect = this.splashImage.width / this.splashImage.height;
      
      let drawWidth = maxWidth;
      let drawHeight = drawWidth / imgAspect;
      
      if (drawHeight > maxHeight) {
        drawHeight = maxHeight;
        drawWidth = drawHeight * imgAspect;
      }
      
      // Center the image
      const offsetX = (renderer.width - drawWidth) / 2;
      const offsetY = 150; // Position near top
      
      // Draw the transparent image
      ctx.drawImage(this.splashImage, offsetX, offsetY, drawWidth, drawHeight);
    }
    
    // Draw Backtrax logo in center/middle of canvas
    if (this.backtraxLogoLoaded && this.backtraxLogo) {
      const logoMaxWidth = renderer.width * 0.7;
      const logoMaxHeight = renderer.height * 0.4;
      const logoAspect = this.backtraxLogo.width / this.backtraxLogo.height;
      
      let logoDrawWidth = logoMaxWidth;
      let logoDrawHeight = logoDrawWidth / logoAspect;
      
      if (logoDrawHeight > logoMaxHeight) {
        logoDrawHeight = logoMaxHeight;
        logoDrawWidth = logoDrawHeight * logoAspect;
      }
      
      // Position in the center/middle of the canvas
      const logoX = (renderer.width - logoDrawWidth) / 2;
      const logoY = (renderer.height - logoDrawHeight) / 2;
      
      // Draw the Backtrax logo
      ctx.drawImage(this.backtraxLogo, logoX, logoY, logoDrawWidth, logoDrawHeight);
    }

    if (this.showInstructions) {
      this.renderInstructions(ctx);
      return;
    }

    const centerX = renderer.width / 2;

    // Glowing title with retro effect
    ctx.save();
    ctx.translate(centerX, Math.min(this.logoY, 80));
    ctx.scale(this.logoScale, this.logoScale);
    
    // Outer glow
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 30;
    ctx.font = 'bold 42px "Courier New"';
    ctx.fillStyle = '#00ffff';
    ctx.textAlign = 'center';
    ctx.fillText('HANDS ACROSS', 0, 0);
    
    ctx.font = 'bold 36px "Courier New"';
    ctx.fillStyle = '#ff00ff';
    ctx.shadowColor = '#ff00ff';
    ctx.fillText('AMERICA', 0, 45);
    
    ctx.restore();

    // Subtitle
    ctx.shadowBlur = 0;
    ctx.font = '14px "Courier New"';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.globalAlpha = 0.8;
    ctx.fillText('Kid Kelly\'s Retro Arcade Edition', centerX, 165);
    ctx.globalAlpha = 1;

    // Menu options with neon glow
    const startY = 500;
    const spacing = 50;

    for (let i = 0; i < this.options.length; i++) {
      const y = startY + i * spacing;
      const isSelected = i === this.selectedOption;

      if (isSelected) {
        const pulse = 0.7 + Math.sin(this.animTimer * 5) * 0.3;
        ctx.globalAlpha = pulse;
        
        // Neon arrows
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 15;
        ctx.fillStyle = '#00ffff';
        ctx.font = 'bold 20px "Courier New"';
        ctx.fillText('►', centerX - 100, y);
        ctx.fillText('◄', centerX + 100, y);
      }

      // Option text
      ctx.shadowColor = isSelected ? '#00ffff' : 'transparent';
      ctx.shadowBlur = isSelected ? 20 : 0;
      ctx.font = isSelected ? 'bold 20px "Courier New"' : '18px "Courier New"';
      ctx.fillStyle = isSelected ? '#00ffff' : '#ffffff';
      ctx.globalAlpha = isSelected ? 1 : 0.7;
      ctx.fillText(this.options[i], centerX, y);
    }

    // Controls hint at bottom
    ctx.shadowBlur = 0;
    ctx.font = '12px "Courier New"';
    ctx.fillStyle = '#00ffff';
    ctx.globalAlpha = 0.5 + Math.sin(this.animTimer * 3) * 0.3;
    ctx.fillText('↑↓ Navigate  •  ENTER Start  •  SPACE Fire', centerX, renderer.height - 40);
    ctx.globalAlpha = 1;
  }

  private renderInstructions(ctx: CanvasRenderingContext2D): void {
    const renderer = this.game.getRenderer();
    const centerX = renderer.width / 2;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.fillRect(0, 0, renderer.width, renderer.height);

    // Title
    ctx.font = 'bold 24px "Courier New"';
    ctx.fillStyle = COLORS.PRIMARY;
    ctx.textAlign = 'center';
    ctx.fillText('INSTRUCTIONS', centerX, 50);

    // Instructions
    ctx.font = '14px "Courier New"';
    ctx.fillStyle = COLORS.TEXT;
    ctx.textAlign = 'left';

    const instructions = [
      'CONTROLS:',
      '  ← → or A/D - Move left/right',
      '  SPACE or Z - Shoot',
      '  ESC - Pause',
      '',
      'OBJECTIVE:',
      '  Defend America against hardships!',
      '  Complete waves to build the chain',
      '  Defeat bosses to secure each region',
      '',
      'ENEMIES:',
      '  Hunger Bugs - Fast zigzag movement',
      '  Shadows - Fade in and out',
      '  Gremlins - Diving attacks',
      '  Clouds - Drop hazards',
      '',
      'POWER-UPS:',
      '  Hope Beam - Piercing shots',
      '  Neon Shield - Protection',
      '  Score Multiplier - Double points',
      '  Wide Beam - 3-way shot',
      '',
      'Press ENTER to return to menu',
    ];

    let y = 100;
    for (const line of instructions) {
      ctx.fillText(line, 40, y);
      y += 25;
    }
  }
}
